// calculations.js — All math functions and formatters for Market Watch
// Pure functions with no React dependency — can be used by any component

// ═══════════════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════════════

// Date → "10 Mar 2026"
export function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

// Indian rupee format: ₹1,23,456
export function fmtINR(n) {
  if (n == null) return '₹0';
  return '₹' + Number(Math.round(n)).toLocaleString('en-IN');
}

// Indian number format (no ₹): 1,23,456
export function fmtNum(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('en-IN');
}

// ═══════════════════════════════════════════════════════════
// IMPLIED VOLATILITY — Black-Scholes Newton-Raphson
// Risk-free rate 7% (India), Dividend yield 0%
// ═══════════════════════════════════════════════════════════

function normalCDF(x) {
  if (x > 10) return 1;
  if (x < -10) return 0;
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x) / Math.SQRT2;
  const t = 1.0 / (1.0 + p * ax);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax);
  return 0.5 * (1.0 + sign * y);
}

function normalPDF(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

function bsPrice(S, K, T, r, sigma, isCall) {
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);
  if (isCall) return S * normalCDF(d1) - K * Math.exp(-r * T) * normalCDF(d2);
  return K * Math.exp(-r * T) * normalCDF(-d2) - S * normalCDF(-d1);
}

function bsVega(S, K, T, r, sigma) {
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
  return S * Math.sqrt(T) * normalPDF(d1);
}

export function calcIV(spot, strike, optionPrice, daysToExpiry, type) {
  if (!spot || !strike || !optionPrice || !daysToExpiry) return 0;
  if (optionPrice <= 0 || daysToExpiry <= 0) return 0;
  const T = daysToExpiry / 365;
  const r = 0.07;
  const isCall = type === 'CE';
  const intrinsic = isCall ? Math.max(0, spot - strike * Math.exp(-r * T)) : Math.max(0, strike * Math.exp(-r * T) - spot);
  if (optionPrice <= intrinsic) return 0;
  let sigma = 0.3;
  for (let i = 0; i < 100; i++) {
    const price = bsPrice(spot, strike, T, r, sigma, isCall);
    const vega = bsVega(spot, strike, T, r, sigma);
    if (Math.abs(vega) < 1e-12) break;
    const diff = price - optionPrice;
    if (Math.abs(diff) < 0.0001) break;
    sigma = sigma - diff / vega;
    if (sigma < 0.01) sigma = 0.01;
    if (sigma > 5.0) sigma = 5.0;
  }
  return +(sigma * 100).toFixed(1);
}

// Strike/Stock ratio = |(Strike / Spot) - 1| × 100 — % distance of the strike from the underlying
// spot. Shared by Market Watch (computeRow) and the Positions page (computePositionRow) so the
// formula lives in exactly one place.
export function calcStrikeStockRatio(strike, spot) {
  return spot > 0 ? +(Math.abs((strike / spot) - 1) * 100).toFixed(1) : 0;
}

// ═══════════════════════════════════════════════════════════
// COMPUTE ROW — Takes raw option + live data, returns
// a fully computed row ready for display in the table
// ═══════════════════════════════════════════════════════════

export function computeRow(option, ticks, margins, results) {
  const tick = ticks[option.token] || {};
  const ltp = tick.ltp || 0;
  // Spot price: always check the underlying token directly — don't gate on option tick
  // Underlying tokens get WebSocket priority so spot is almost always available,
  // even when the option itself hasn't received a tick yet (e.g., on REST polling)
  const spot = ticks[option.underlyingToken]?.ltp || option.spot || 0;
  const margin = margins[option.tradingsymbol]?.totalMargin || 0;
  const resultInfo = results[option.symbol] || null;

  // Days to expiry
  const expiryDate = new Date(option.expiry);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiryDate.setHours(0, 0, 0, 0);
  const daysToExpiry = Math.max(0, Math.ceil((expiryDate - today) / 86400000));

  // Strike/Stock ratio = |(Strike / Spot) - 1| × 100  (shared helper)
  const strikeStockRatio = calcStrikeStockRatio(option.strike, spot);

  // Total Premium = LTP × Lot Size
  const premiumIncome = Math.round(ltp * (option.lotSize || 0));

  // Return %
  const returnPct = margin > 0 ? +((premiumIncome / margin) * 100).toFixed(2) : 0;

  // Annualized return
  let annualized = 0;
  if (daysToExpiry > 0 && returnPct > 0) {
    annualized = +((Math.pow(1 + Math.abs(returnPct / 100), 365 / daysToExpiry) - 1) * 100).toFixed(1);
  }

  // Equal weight
  const equalWeight = +(Math.abs(annualized * strikeStockRatio)).toFixed(1);

  // IV (Black-Scholes)
  const iv = calcIV(spot, option.strike, ltp, daysToExpiry, option.type);

  // Result date — re-evaluate "is it still upcoming?" against TODAY every time; never trust the
  // stored isFutureResult flag (it goes stale the moment a date passes). Grey = a past result
  // (info only); Yellow = an upcoming result; Red dot = an upcoming result on/before this option's
  // expiry. A PAST result never gets a red dot. (Mirrors backend computeRows.js.)
  const _nextResult = resultInfo?.nextResultDate ? new Date(resultInfo.nextResultDate) : null;
  const isFutureResult = !!(_nextResult && _nextResult >= today);
  const resultDate = resultInfo?.displayDate || null;
  const resultDateISO = resultInfo?.displayDateISO || '';
  const resultsBeforeExpiry = isFutureResult && _nextResult <= expiryDate;

  // Best Buy/Sell — top 1 level from market depth
  const depth = tick.depth || null;
  const bestBuy = depth?.buy?.[0] || null;
  const bestSell = depth?.sell?.[0] || null;

  // Extrinsic Value / CMP ratio (for Ext/CMP column)
  // OTM: entire premium is extrinsic
  // ITM CE: extrinsic = LTP + Strike - CMP
  // ITM PE: extrinsic = LTP - Strike + CMP
  let extrinsic = ltp;
  if (spot > 0 && ltp > 0) {
    const isCall = option.type === 'CE';
    const isITM = isCall ? option.strike < spot : option.strike > spot;
    if (isITM) {
      extrinsic = isCall ? (ltp + option.strike - spot) : (ltp - option.strike + spot);
      if (extrinsic < 0) extrinsic = 0;
    }
  }

  return {
    // Static (from backend)
    id: option.token,
    token: option.token,
    symbol: option.symbol,
    underlying: option.underlying || option.symbol,
    expiry: option.expiry,
    expiryStr: option.expiryStr || formatDate(option.expiry),
    type: option.type,
    strike: option.strike,
    lotSize: option.lotSize || 0,
    tradingsymbol: option.tradingsymbol,
    sector: option.sector || '',
    category: option.category || '',
    publicPrivate: option.publicPrivate || '',
    isIndex: option.isIndex || false,

    // Live (from WebSocket ticks)
    ltp,
    spot,
    volume: tick.volume || 0,
    oi: tick.oi || 0,
    source: tick.source || 'none',

    // Best Buy/Sell
    bestBuyPrice: bestBuy?.price || 0,
    bestBuyQty: bestBuy?.quantity || 0,
    bestSellPrice: bestSell?.price || 0,
    bestSellQty: bestSell?.quantity || 0,
    depth: depth,

    // Calculated
    daysToExpiry,
    strikeStockRatio,
    ltpCmp: spot > 0 ? +((extrinsic / spot) * 100).toFixed(2) : 0,
    premiumIncome,
    margin,
    returnPct,
    annualized,
    equalWeight,
    iv,

    // Result date
    resultDate,
    resultDateISO,
    isFutureResult,
    resultsBeforeExpiry,

    // Position (Phase 9)
    hasPosition: false
  };
}
